import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "reactstrap"
import CreatableSelect from "react-select/creatable"
import Breadcrumbs from "components/Common/Breadcrumb"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import {
  useCreateVehicleStockInventory,
  useEditVehicleStockInventory,
  useVehicleStockInventory,
} from "hooks/useVehicleStockInventory"
import { useGetSendersList, useGetReceiversList } from "hooks/useInventory"
import { useVehicleStock, useVehicleSearchQuery } from "hooks/stockHook"
import { useShowrooms } from "hooks/showroomHook"
import { useAllEmployeeDesignations } from "hooks/useEmployeeDesignation"
import {
  useInventoryAccessories,
  useCreateInventoryAccessory,
} from "hooks/useInventoryAccessory"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import { getVehicleStockInventoryPdf } from "api/vehicleStockInventoryApi"
import "./inventory-page.scss"

const EMPTY_VEHICLE_INFO = {
  vehicle_name: "",
  chassis_no: "",
  color: "",
  engine_no: "",
  model_year: "",
  fuel_type: "",
  tyre_size: "",
  cc: "",
  mileage: "",
}

const EMPTY_PARTY = { name: "", mobile: "", designation: "", company: "" }

// Build form items list by merging master accessories (from DB) with stored
// values from editedData. New accessories appended after master order.
const buildItems = (accessories, editedData) => {
  const stored = Array.isArray(editedData?.items) ? editedData.items : []
  const masterList = Array.isArray(accessories) ? accessories : []

  const storedByName = new Map()
  const storedById = new Map()
  stored.forEach(row => {
    if (row?.name) {
      storedByName.set(String(row.name).toLowerCase(), row)
    }
    if (row?.id != null) {
      storedById.set(Number(row.id), row)
    }
  })

  const out = []
  const usedNames = new Set()

  masterList.forEach(acc => {
    const stockedRow =
      storedById.get(Number(acc.id)) ||
      storedByName.get(String(acc.name).toLowerCase())
    usedNames.add(String(acc.name).toLowerCase())
    out.push({
      id: acc.id,
      name: acc.name,
      value: stockedRow?.value ?? null,
      remarks: stockedRow?.remarks ?? "",
    })
  })

  // Stored items not in master list (legacy or deleted accessories) — keep them
  stored.forEach(row => {
    if (!row?.name) return
    if (usedNames.has(String(row.name).toLowerCase())) return
    out.push({
      id: row.id ?? null,
      name: row.name,
      value: row.value ?? null,
      remarks: row.remarks ?? "",
    })
  })

  return out
}

const deriveVehicleInfoFromStock = stock => {
  if (!stock) return { ...EMPTY_VEHICLE_INFO }
  const brand = stock.brand?.name || ""
  const model = stock.model?.name || ""
  return {
    vehicle_name: `${brand} ${model}`.trim(),
    chassis_no: stock.full_chassis || "",
    color: stock.exterior_color?.name || "",
    engine_no: stock.full_engine || "",
    model_year: stock.year?.name || "",
    fuel_type: stock.fuel_type?.name || "",
    tyre_size: stock.tire_size || "",
    cc: stock.engine_cc?.name || "",
    mileage: stock.mileage || "",
  }
}

const splitItems = items => {
  const half = Math.ceil(items.length / 2)
  return [items.slice(0, half), items.slice(half)]
}

const InventoryAddEditPage = () => {
  const navigate = useNavigate()
  const { id: routeId } = useParams()
  const [searchParams] = useSearchParams()
  const queryVehicleId = searchParams.get("vehicle_id")

  const isEdit = !!routeId

  document.title = isEdit ? "Edit Inventory" : "Add Inventory"

  // Load record on edit
  const { data: existingResponse, isLoading: loadingExisting } =
    useVehicleStockInventory(routeId, isEdit)
  const editedData = existingResponse?.data || null

  // Vehicle searched/picked on this page (chassis or stock ID). Drives both the
  // vehicle_stock_id link and the full-detail fetch used to autofill.
  const [pickedStockId, setPickedStockId] = useState(null)
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("")

  const vehicleLookupId = useMemo(() => {
    if (isEdit) {
      return editedData?.vehicle_stock?.stock_id || editedData?.vehicle_stock_id
    }
    return pickedStockId || queryVehicleId
  }, [isEdit, editedData, pickedStockId, queryVehicleId])

  const { data: stockResponse } = useVehicleStock(vehicleLookupId)
  const stockData = stockResponse?.data || null

  const { data: vehicleSearchResults = [], isFetching: vehicleSearching } =
    useVehicleSearchQuery(vehicleSearchTerm)

  const vehicleSearchOptions = useMemo(() => {
    return (vehicleSearchResults || []).map(v => ({
      value: String(v.id),
      label: `${v.stock_id || ""}${v.full_chassis ? ` - ${v.full_chassis}` : ""}`,
      raw: v,
    }))
  }, [vehicleSearchResults])

  const handleVehiclePick = option => {
    if (option && option.value && !option.__isNew__) {
      // Existing vehicle selected — link it and refill from full details.
      formik.setFieldValue("vehicle_stock_id", option.value)
      formik.setFieldValue("vehicle_info", { ...EMPTY_VEHICLE_INFO })
      setPickedStockId(option.value)
    } else {
      // Cleared — back to manual entry.
      formik.setFieldValue("vehicle_stock_id", "")
      setPickedStockId(null)
    }
  }

  const handleVehicleCreate = inputValue => {
    // Typed a chassis/stock not in stock — onboard as new on submit.
    formik.setFieldValue("vehicle_stock_id", "")
    setPickedStockId(null)
    formik.setFieldValue("vehicle_info", {
      ...formik.values.vehicle_info,
      chassis_no: inputValue,
    })
  }

  const { data: sendersData } = useGetSendersList()
  const { data: receiversData } = useGetReceiversList()

  // Dynamic inventory accessories master list
  const { data: accessoriesResponse, refetch: refetchAccessories } =
    useInventoryAccessories({ all: true, active_only: true })
  const accessoriesList = useMemo(() => {
    return accessoriesResponse?.data?.data || accessoriesResponse?.data || []
  }, [accessoriesResponse])

  const { mutateAsync: createAccessoryMutation, isPending: addAccessoryLoading } =
    useCreateInventoryAccessory()

  const { hasAnyPermission } = useAuth()
  const canAddAccessory = hasAnyPermission([ACCESS.INVENTORY_ACCESSORY.ADD])

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateVehicleStockInventory()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditVehicleStockInventory()

  const [pdfLoading, setPdfLoading] = useState(false)
  const [newAccessoryName, setNewAccessoryName] = useState("")

  const senderOptions = useMemo(() => {
    const list = sendersData?.data?.data || sendersData?.data || []
    return list.map(s => ({ value: String(s.id), label: s.name }))
  }, [sendersData])

  const receiverOptions = useMemo(() => {
    const list = receiversData?.data?.data || receiversData?.data || []
    return list.map(s => ({ value: String(s.id), label: s.name }))
  }, [receiversData])

  const { data: showroomsData } = useShowrooms({ all: true })
  const showroomOptions = useMemo(() => {
    const list = showroomsData?.data?.data || showroomsData?.data || []
    return list.map(s => ({ value: s.name, label: s.name }))
  }, [showroomsData])

  const { data: designationsData } = useAllEmployeeDesignations()
  const designationOptions = useMemo(() => {
    const list = designationsData?.data?.data || designationsData?.data || []
    return list.map(d => ({ value: d.title, label: d.title }))
  }, [designationsData])

  const validationSchema = Yup.object({})

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_stock_id:
        editedData?.vehicle_stock?.id ||
        editedData?.vehicle_stock_id ||
        queryVehicleId ||
        "",
      inventory_date:
        editedData?.inventory_date?.substring(0, 16) ||
        new Date().toISOString().substring(0, 16),
      sender_id: editedData?.sender_id ? String(editedData.sender_id) : "",
      receiver_id: editedData?.receiver_id
        ? String(editedData.receiver_id)
        : "",
      is_swapped: editedData?.is_swapped ?? true,
      sender_info: { ...EMPTY_PARTY, ...(editedData?.sender_info || {}) },
      receiver_info: { ...EMPTY_PARTY, ...(editedData?.receiver_info || {}) },
      vehicle_info: editedData?.vehicle_info
        ? { ...EMPTY_VEHICLE_INFO, ...editedData.vehicle_info }
        : { ...EMPTY_VEHICLE_INFO },
      remarks: editedData?.remarks || "",
      auditor_note: editedData?.auditor_note || "",
      update_vehicle: false,
      items: buildItems(accessoriesList, editedData),
    },
    validationSchema,
    onSubmit: async values => {
      try {
        const items = values.items
          .filter(i => i?.name)
          .map(i => ({
            id: i.id,
            name: i.name,
            value: i.value || null,
            remarks: i.remarks || "",
          }))

        const payload = {
          vehicle_stock_id: values.vehicle_stock_id,
          inventory_date: values.inventory_date || null,
          sender_id: values.sender_id ? Number(values.sender_id) : null,
          receiver_id: values.receiver_id ? Number(values.receiver_id) : null,
          is_swapped: values.is_swapped,
          sender_info: values.sender_info,
          receiver_info: values.receiver_info,
          vehicle_info: values.vehicle_info,
          update_vehicle: values.update_vehicle,
          remarks: values.remarks || null,
          auditor_note: values.auditor_note || null,
          items,
        }

        const res = isEdit
          ? await editMutation({ id: routeId, ...payload })
          : await createMutation(payload)

        toast.success(
          res?.message ??
            `Inventory ${isEdit ? "updated" : "created"} successfully.`
        )
        navigate(-1)
      } catch (error) {
        console.error(error)
        toast.error("Failed to save inventory.")
      }
    },
  })

  // Autofill vehicle_info from stock: on add (no info yet), or in edit once the
  // user picks a different vehicle to relink (pickedStockId set).
  useEffect(() => {
    if (isEdit && !pickedStockId) return
    if (!stockData) return
    const current = formik.values.vehicle_info || {}
    const hasAny = Object.values(current).some(
      v => v && String(v).trim() !== ""
    )
    if (hasAny) return
    formik.setFieldValue("vehicle_info", deriveVehicleInfoFromStock(stockData))
  }, [stockData, isEdit, pickedStockId])

  // Re-merge items when accessories master list changes (e.g. new accessory created inline)
  useEffect(() => {
    if (!accessoriesList?.length) return
    const merged = buildItems(
      accessoriesList,
      // Treat current form state as the "edited" record so unsaved values persist
      { items: formik.values.items }
    )
    // Only update if length differs to avoid render loop on identical lists
    if (merged.length !== formik.values.items.length) {
      formik.setFieldValue("items", merged)
    }
  }, [accessoriesList])

  // Autofill sender_info on sender select
  useEffect(() => {
    if (!formik.values.sender_id || !sendersData) return
    const list = sendersData?.data?.data || sendersData?.data || []
    const sender = list.find(s => String(s.id) === String(formik.values.sender_id))
    if (!sender) return
    formik.setFieldValue("sender_info", {
      name: sender.name || "",
      mobile: Array.isArray(sender.mobile)
        ? sender.mobile[0]
        : sender.mobile || formik.values.sender_info.mobile || "",
      designation:
        sender.designation?.title ||
        sender.designation ||
        formik.values.sender_info.designation ||
        "",
      company:
        sender.showroom?.name ||
        sender.company ||
        formik.values.sender_info.company ||
        "",
    })
  }, [formik.values.sender_id, sendersData])

  // Autofill receiver_info on receiver select
  useEffect(() => {
    if (!formik.values.receiver_id || !receiversData) return
    const list = receiversData?.data?.data || receiversData?.data || []
    const r = list.find(s => String(s.id) === String(formik.values.receiver_id))
    if (!r) return
    formik.setFieldValue("receiver_info", {
      name: r.name || "",
      mobile: Array.isArray(r.mobile) ? r.mobile[0] : r.mobile || "",
      designation: r.designation || "",
      company: r.importer?.name || "",
    })
  }, [formik.values.receiver_id, receiversData])

  const handleItemRemarkChange = (idx, value) => {
    const next = formik.values.items.map((row, i) =>
      i === idx ? { ...row, remarks: value } : row
    )
    formik.setFieldValue("items", next)
  }

  const handleItemValueChange = (idx, value) => {
    const next = formik.values.items.map((row, i) => {
      if (i !== idx) return row
      return { ...row, value: row.value === value ? null : value }
    })
    formik.setFieldValue("items", next)
  }

  const handlePartyChange = (party, key, value) => {
    formik.setFieldValue(party, { ...formik.values[party], [key]: value })
  }

  const handleVehicleInfoChange = (key, value) => {
    formik.setFieldValue("vehicle_info", {
      ...formik.values.vehicle_info,
      [key]: value,
    })
  }

  const handleResetVehicleInfo = () => {
    if (!stockData) return
    formik.setFieldValue("vehicle_info", deriveVehicleInfoFromStock(stockData))
  }

  const handleAddAccessory = async () => {
    const name = newAccessoryName.trim()
    if (!name) return
    try {
      const res = await createAccessoryMutation({
        name,
        is_active: true,
      })
      const created = res?.data
      if (created) {
        // Append to form items immediately
        formik.setFieldValue("items", [
          ...formik.values.items,
          {
            id: created.id,
            name: created.name,
            value: null,
            remarks: "",
          },
        ])
      }
      setNewAccessoryName("")
      toast.success(res?.message ?? "Accessory added.")
      refetchAccessories()
    } catch (err) {
      console.error(err)
      const msg =
        err?.response?.data?.errors?.name?.[0] ||
        err?.response?.data?.message ||
        "Failed to add accessory."
      toast.error(msg)
    }
  }

  const handleDownloadPdf = async () => {
    if (!routeId) return
    try {
      await getVehicleStockInventoryPdf(routeId, setPdfLoading)
    } catch (err) {
      console.error(err)
      toast.error("Failed to generate PDF.")
      setPdfLoading(false)
    }
  }

  if (isEdit && loadingExisting) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    )
  }

  const [leftItems, rightItems] = splitItems(formik.values.items)
  const rowCount = Math.max(leftItems.length, rightItems.length)
  const saving = createLoading || editLoading

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Inventory"
          breadcrumbItem={isEdit ? "Edit Inventory" : "Add Inventory"}
        />

        <div className="d-flex justify-content-end mb-3 gap-2">
          <Button color="light" onClick={() => navigate(-1)}>
            <i className="mdi mdi-arrow-left me-1" />
            Back
          </Button>
          {isEdit && (
            <Button
              color="info"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
            >
              {pdfLoading ? (
                <i className="bx bx-loader bx-spin me-1" />
              ) : (
                <i className="mdi mdi-download me-1" />
              )}
              Download PDF
            </Button>
          )}
          <Button color="success" onClick={formik.handleSubmit} disabled={saving}>
            <i className="mdi mdi-content-save me-1" />
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Inventory"}
          </Button>
        </div>

        <Form
          onSubmit={e => {
            e.preventDefault()
            formik.handleSubmit()
          }}
          className="inventory-doc"
        >
          {/* Company Header */}
          <div className="inv-header">
            <h1 className="inv-company">
              AUROBIL <span className="inv-accent">CARS</span>
            </h1>
            <div className="inv-address">
              279/6/B, Matikata Kalshi Road, Manikdi, Mirpur, Dhaka Cantonment,
              Dhaka-1206
            </div>
            <div className="inv-phones">01911-887-731, 01911-773-331</div>
            <div className="inv-title-pill">Inventory</div>
          </div>

          <div className="inv-meta-line">
            <span className="inv-meta-label">Date &amp; Time:</span>
            <input
              type="datetime-local"
              className="inv-date-input"
              value={formik.values.inventory_date}
              onChange={e =>
                formik.setFieldValue("inventory_date", e.target.value)
              }
            />
            <Button
              type="button"
              color="info"
              outline
              size="sm"
              className="ms-3"
              onClick={() =>
                formik.setFieldValue("is_swapped", !formik.values.is_swapped)
              }
            >
              <i className="mdi mdi-swap-horizontal me-1" />
              Swap sender ↔ receiver
            </Button>
          </div>

          {/* Sender + Receiver Row */}
          <Row className="g-3 mt-2">
            <Col md={6}>
              <div className="inv-section-title">
                {formik.values.is_swapped
                  ? "Receiver Information"
                  : "Sender Information"}
              </div>
              <div className="mb-2">
                <FormikCreatableSelect
                  name="sender_id"
                  label={
                    formik.values.is_swapped
                      ? "Select Receiver"
                      : "Select Sender"
                  }
                  formik={formik}
                  options={senderOptions}
                  placeholder="Select sender..."
                />
              </div>
              <PartyFields
                values={formik.values.sender_info}
                onChange={(k, v) => handlePartyChange("sender_info", k, v)}
                designationField={
                  <div className="inv-kv-row inv-kv-row-select">
                    <div className="inv-kv-label">Designation</div>
                    <div className="inv-kv-colon">:</div>
                    <div className="inv-kv-select-wrap">
                      <FormikCreatableSelect
                        name="sender_info.designation"
                        formik={formik}
                        options={designationOptions}
                        placeholder="Select or type designation..."
                      />
                    </div>
                  </div>
                }
                companyField={
                  <div className="inv-kv-row inv-kv-row-select">
                    <div className="inv-kv-label">Company/Showroom</div>
                    <div className="inv-kv-colon">:</div>
                    <div className="inv-kv-select-wrap">
                      <FormikCreatableSelect
                        name="sender_info.company"
                        formik={formik}
                        options={showroomOptions}
                        placeholder="Select or type showroom..."
                      />
                    </div>
                  </div>
                }
              />
            </Col>
            <Col md={6}>
              <div className="inv-section-title">
                {formik.values.is_swapped
                  ? "Sender Information"
                  : "Receiver Information"}
              </div>
              <div className="mb-2">
                <FormikCreatableSelect
                  name="receiver_id"
                  label="Select Receiver"
                  formik={formik}
                  options={receiverOptions}
                  placeholder="Select receiver..."
                />
              </div>
              <PartyFields
                values={formik.values.receiver_info}
                onChange={(k, v) => handlePartyChange("receiver_info", k, v)}
              />
            </Col>
          </Row>

          {/* Vehicle Information */}
          <div className="d-flex align-items-center mt-4">
            <div className="inv-section-title flex-grow-1">
              Vehicle Information
            </div>
            {stockData && !isEdit && (
              <Button
                type="button"
                color="link"
                size="sm"
                onClick={handleResetVehicleInfo}
                className="p-0"
              >
                <i className="mdi mdi-refresh me-1" />
                Reset from stock
              </Button>
            )}
          </div>

          <div className="mb-2">
            <CreatableSelect
              isClearable
              options={vehicleSearchOptions}
              isLoading={vehicleSearching}
              onInputChange={val => setVehicleSearchTerm(val)}
              onChange={handleVehiclePick}
              onCreateOption={handleVehicleCreate}
              placeholder={
                isEdit
                  ? "Change vehicle — search Chassis No. or Stock ID"
                  : "Search vehicle by Chassis No. or Stock ID — or type a new one"
              }
              formatCreateLabel={input => `New vehicle: ${input}`}
              noOptionsMessage={() =>
                vehicleSearchTerm.length < 2
                  ? "Type at least 2 characters"
                  : "No match — type to add as new"
              }
              styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
            />
            <small className="text-muted">
              Found: autofills below (editable). Not found: fill the fields and
              it’s onboarded automatically on save.
            </small>
          </div>

          {isEdit && formik.values.vehicle_stock_id && (
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="update_vehicle"
                checked={formik.values.update_vehicle}
                onChange={e =>
                  formik.setFieldValue("update_vehicle", e.target.checked)
                }
              />
              <label className="form-check-label" htmlFor="update_vehicle">
                Update the linked vehicle record with these details (fixes the
                vehicle everywhere, not just this inventory)
              </label>
            </div>
          )}
          <div className="inv-kv-grid">
            <KVRow
              left={{
                label: "Vehicle Name",
                value: formik.values.vehicle_info.vehicle_name,
                onChange: v => handleVehicleInfoChange("vehicle_name", v),
              }}
              right={{
                label: "Chassis No.",
                value: formik.values.vehicle_info.chassis_no,
                onChange: v => handleVehicleInfoChange("chassis_no", v),
              }}
            />
            <KVRow
              left={{
                label: "Color",
                value: formik.values.vehicle_info.color,
                onChange: v => handleVehicleInfoChange("color", v),
              }}
              right={{
                label: "Engine No.",
                value: formik.values.vehicle_info.engine_no,
                onChange: v => handleVehicleInfoChange("engine_no", v),
              }}
            />
            <KVRow
              left={{
                label: "Model/Year",
                value: formik.values.vehicle_info.model_year,
                onChange: v => handleVehicleInfoChange("model_year", v),
              }}
              right={{
                label: "Fuel Type",
                value: formik.values.vehicle_info.fuel_type,
                onChange: v => handleVehicleInfoChange("fuel_type", v),
              }}
            />
            <KVRow
              left={{
                label: "Tyre Size",
                value: formik.values.vehicle_info.tyre_size,
                onChange: v => handleVehicleInfoChange("tyre_size", v),
              }}
              right={{
                label: "CC",
                value: formik.values.vehicle_info.cc,
                onChange: v => handleVehicleInfoChange("cc", v),
                extraLabel: "Mileage (KM)",
                extraValue: formik.values.vehicle_info.mileage,
                onExtraChange: v => handleVehicleInfoChange("mileage", v),
              }}
            />
          </div>

          {/* Accessories */}
          <div className="d-flex align-items-center justify-content-between mt-4 flex-wrap gap-2">
            <div className="inv-section-title m-0">Accessories</div>
            {canAddAccessory && (
              <div className="d-flex gap-2 align-items-center inv-add-accessory">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="New accessory name..."
                  value={newAccessoryName}
                  onChange={e => setNewAccessoryName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddAccessory()
                    }
                  }}
                  style={{ minWidth: 200 }}
                />
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  onClick={handleAddAccessory}
                  disabled={!newAccessoryName.trim() || addAccessoryLoading}
                >
                  <i className="mdi mdi-plus me-1" />
                  Add
                </Button>
              </div>
            )}
          </div>

          {/* Desktop / tablet: 2-column table */}
          <div className="inv-items-wrap d-none d-md-block">
            <table className="inv-items-table">
              <thead>
                <tr>
                  <th style={{ width: "4%" }}>SL</th>
                  <th style={{ width: "20%" }}>Items Name</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th style={{ width: "14%" }}>Remarks</th>
                  <th style={{ width: "4%" }}>SL</th>
                  <th style={{ width: "20%" }}>Items Name</th>
                  <th style={{ width: "12%" }}>Status</th>
                  <th style={{ width: "14%" }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, i) => {
                  const left = leftItems[i]
                  const right = rightItems[i]
                  const leftIdx = i
                  const rightIdx = leftItems.length + i
                  return (
                    <tr key={i}>
                      <td className="text-center">{left?.id || ""}</td>
                      <td>{left?.name || ""}</td>
                      <td>
                        {left && (
                          <YesNoToggle
                            value={left.value}
                            onChange={v => handleItemValueChange(leftIdx, v)}
                          />
                        )}
                      </td>
                      <td>
                        {left && (
                          <input
                            type="text"
                            className="inv-cell-input"
                            value={left.remarks || ""}
                            onChange={e =>
                              handleItemRemarkChange(leftIdx, e.target.value)
                            }
                          />
                        )}
                      </td>
                      <td className="text-center">{right?.id || ""}</td>
                      <td>{right?.name || ""}</td>
                      <td>
                        {right && (
                          <YesNoToggle
                            value={right.value}
                            onChange={v => handleItemValueChange(rightIdx, v)}
                          />
                        )}
                      </td>
                      <td>
                        {right && (
                          <input
                            type="text"
                            className="inv-cell-input"
                            value={right.remarks || ""}
                            onChange={e =>
                              handleItemRemarkChange(rightIdx, e.target.value)
                            }
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked card list */}
          <div className="d-md-none">
            {formik.values.items.map((item, idx) => (
              <div className="inv-item-card" key={item.id ?? idx}>
                <div className="inv-item-card-head">
                  <span className="inv-item-sl">{item.id || idx + 1}</span>
                  <span className="inv-item-name">{item.name}</span>
                </div>
                <div className="inv-item-row">
                  <span className="inv-item-label">Status</span>
                  <YesNoToggle
                    value={item.value}
                    onChange={v => handleItemValueChange(idx, v)}
                  />
                </div>
                <div className="inv-item-row">
                  <span className="inv-item-label">Remarks</span>
                  <input
                    type="text"
                    className="inv-cell-input inv-cell-input--bordered"
                    value={item.remarks || ""}
                    onChange={e =>
                      handleItemRemarkChange(idx, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Remarks */}
          <div className="mt-4">
            <div className="inv-section-title">Remarks</div>
            <textarea
              className="inv-cell-input"
              rows={2}
              style={{ width: "100%", minHeight: 48 }}
              value={formik.values.remarks}
              onChange={e => formik.setFieldValue("remarks", e.target.value)}
            />
          </div>

          {/* Auditor's Note */}
          <div className="mt-3">
            <div className="inv-section-title">Auditor's Note:</div>
            <textarea
              className="inv-cell-input"
              rows={2}
              style={{ width: "100%", minHeight: 48 }}
              value={formik.values.auditor_note}
              onChange={e =>
                formik.setFieldValue("auditor_note", e.target.value)
              }
            />
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button color="light" type="button" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button color="success" type="submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Inventory"}
            </Button>
          </div>
        </Form>
      </Container>
    </div>
  )
}

const YesNoToggle = ({ value, onChange }) => (
  <ButtonGroup size="sm" className="inv-yesno-group">
    <Button
      type="button"
      color={value === "yes" ? "success" : "light"}
      outline={value !== "yes"}
      onClick={() => onChange("yes")}
      className="inv-yesno-btn"
    >
      Yes
    </Button>
    <Button
      type="button"
      color={value === "no" ? "danger" : "light"}
      outline={value !== "no"}
      onClick={() => onChange("no")}
      className="inv-yesno-btn"
    >
      No
    </Button>
  </ButtonGroup>
)

const PartyFields = ({
  values,
  onChange,
  designationField = null,
  companyField = null,
}) => (
  <div className="inv-kv-grid">
    <PartyRow label="Name" value={values.name} onChange={v => onChange("name", v)} />
    <PartyRow
      label="Mobile"
      value={values.mobile}
      onChange={v => onChange("mobile", v)}
    />
    {designationField || (
      <PartyRow
        label="Designation"
        value={values.designation}
        onChange={v => onChange("designation", v)}
      />
    )}
    {companyField || (
      <PartyRow
        label="Company/Showroom"
        value={values.company}
        onChange={v => onChange("company", v)}
      />
    )}
  </div>
)

const PartyRow = ({ label, value, onChange }) => (
  <div className="inv-kv-row">
    <div className="inv-kv-label">{label}</div>
    <div className="inv-kv-colon">:</div>
    <input
      type="text"
      className="inv-kv-input"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
    />
  </div>
)

const KVRow = ({ left, right }) => (
  <div className="inv-kv-row inv-kv-row-double">
    <div className="inv-kv-pair">
      <div className="inv-kv-label">{left.label}</div>
      <div className="inv-kv-colon">:</div>
      <input
        type="text"
        className="inv-kv-input"
        value={left.value || ""}
        onChange={e => left.onChange(e.target.value)}
      />
    </div>
    <div className="inv-kv-pair">
      <div className="inv-kv-label">{right.label}</div>
      <div className="inv-kv-colon">:</div>
      <input
        type="text"
        className="inv-kv-input"
        value={right.value || ""}
        onChange={e => right.onChange(e.target.value)}
      />
      {right.extraLabel && (
        <>
          <div className="inv-kv-label ms-2">{right.extraLabel}</div>
          <div className="inv-kv-colon">:</div>
          <input
            type="text"
            className="inv-kv-input"
            value={right.extraValue || ""}
            onChange={e => right.onExtraChange(e.target.value)}
          />
        </>
      )}
    </div>
  </div>
)

export default InventoryAddEditPage
